<template>
  <div class="player-wrapper">
    <video
      :id="id"
      ref="video"
      :controls="controls"
      autoplay
      muted
      playsinline
      class="player-video"
      @playing="$emit('played', $refs.video)"
    ></video>
    <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
  </div>
</template>

<script>
/**
 * WebRtcPlayer — plays the Viz Engine's native WebRTC output (Route A).
 *
 * The engine is the OFFERER: we open a WebSocket to the signaling endpoint the
 * backend hands us — either the engine directly (ws://<host>:<webrtcPort>/ws, dev
 * over plain HTTP) or a per-site TLS proxy (wss://<host>/vizrt/<sigla>/ws, HTTPS
 * environments, where ws:// would be blocked as mixed content). Either way we
 * receive its SDP offer, answer it, trickle ICE both ways, and attach the inbound
 * video track. Only signaling is proxied — media still flows direct from the
 * engine, which is why its own host candidates appear in the SDP.
 *
 * Target engine = Viz Engine 5.4 (WebRTC m125) → standard Unified-Plan /
 * trickle-ICE, no legacy munging.
 *
 * Background-agnostic by design: this component only renders the stream. The page
 * background is owned by the parent — CenaView paints a static black wrapper, which
 * shows through before the first take and in the letterbox bars.
 *
 * Prop/emit shape parallels HlsVideoPlayer.vue (controls/id, played/error) so the
 * two players are interchangeable; CenaView uses this one.
 *
 */
export default {
  name: "WebRtcPlayer",
  props: {
    // Signaling URL for the engine's WebRTC plugin (analogous to HLS `src`).
    // ws:// direct to the engine, or wss:// via the site's TLS proxy.
    signalingUrl: { type: String, default: "" },
    controls: { type: Boolean, default: false },
    id: { type: [String, Number], default: undefined },
    // ICE servers. Default to Google STUN; pass [] for host-only on a closed
    // broadcast LAN, or an internal STUN/TURN if browser↔engine span subnets.
    iceServers: {
      type: Array,
      default: () => [{ urls: "stun:stun.l.google.com:19302" }],
    },
  },
  data() {
    return {
      ws: null,
      pc: null,
      errorMessage: "",
      retryCount: 0,
      maxRetries: 5,
      reconnectTimer: null,
      destroyed: false,
    };
  },
  watch: {
    signalingUrl() {
      this.connect();
    },
  },
  mounted() {
    if (this.signalingUrl) this.connect();
  },
  beforeDestroy() {
    this.destroyed = true;
    this.teardown();
  },
  methods: {
    connect() {
      this.teardown();
      this.errorMessage = "";
      if (!this.signalingUrl) return;

      this.pc = this.buildPeerConnection();

      let socket;
      try {
        socket = new WebSocket(this.signalingUrl);
      } catch (e) {
        this.fail("Falha ao abrir conexão de sinalização.", e);
        return;
      }
      this.ws = socket;

      socket.onopen = () => {
        this.retryCount = 0;
        // Engine is the offerer — nothing to send on open; we wait for its offer.
      };
      socket.onmessage = (ev) => {
        this.handleSignalingMessage(
          typeof ev.data === "string" ? ev.data : ""
        );
      };
      socket.onerror = () => {
        // onclose follows; reconnection is handled there.
        this.$emit("error", { type: "signaling" });
      };
      socket.onclose = () => {
        if (!this.destroyed) this.scheduleReconnect();
      };
    },

    buildPeerConnection() {
      const pc = new RTCPeerConnection({ iceServers: this.iceServers });

      pc.ontrack = (ev) => {
        const stream = ev.streams && ev.streams[0]
          ? ev.streams[0]
          : new MediaStream([ev.track]);
        if (this.$refs.video) {
          this.$refs.video.srcObject = stream;
          // autoplay+muted should start playback; nudge if the browser blocks it
          this.$refs.video.play?.().catch(() => {});
        }
      };

      // The engine opens a data channel for input events; we're display-only,
      // so accept and ignore it (don't let it error the connection).
      pc.ondatachannel = () => {};

      pc.onicecandidate = (ev) => {
        if (ev.candidate) this.sendSignal(this.buildIceMessage(ev.candidate));
      };

      pc.oniceconnectionstatechange = () => {
        const s = pc.iceConnectionState;
        if (s === "failed") {
          this.$emit("error", { type: "ice", state: s });
          if (!this.destroyed) this.scheduleReconnect();
        }
      };

      return pc;
    },

    /**
     * SIGNALING ENVELOPE — INBOUND parsing stays defensive: Vizrt does not publish
     * the exact JSON wrapper, so we recognize an offer / answer / ICE candidate
     * regardless of the field names used, and ignore anything else. OUTBOUND ICE is
     * confirmed against the live engine (see buildIceMessage — flat root, string
     * sdpMid); buildAnswerMessage is not yet verified against a captured sample.
     */
    async handleSignalingMessage(raw) {
      if (!raw || !this.pc) return;

      let msg;
      try {
        msg = JSON.parse(raw);
      } catch {
        return; // non-JSON — capture via the spike if this ever fires
      }

      const type = String(msg.type || msg.messageType || "").toLowerCase();
      const sdp = msg.sdp && (msg.sdp.sdp || msg.sdp);

      const looksLikeOffer =
        type.includes("offer") ||
        (!!sdp && /m=video/.test(sdp) && !type.includes("answer"));
      const looksLikeCandidate = type.includes("candidate") || !!msg.candidate;

      if (looksLikeOffer) {
        await this.answerOffer(sdp);
      } else if (looksLikeCandidate) {
        await this.addRemoteCandidate(msg);
      }
    },

    async answerOffer(sdp) {
      try {
        await this.pc.setRemoteDescription({ type: "offer", sdp });
        const answer = await this.pc.createAnswer();
        await this.pc.setLocalDescription(answer);
        this.sendSignal(this.buildAnswerMessage(this.pc.localDescription.sdp));
      } catch (e) {
        this.fail("Falha ao negociar a sessão WebRTC.", e);
      }
    },

    async addRemoteCandidate(msg) {
      const candidate = msg.candidate && msg.candidate.candidate
        ? msg.candidate
        : {
            candidate: msg.candidate,
            sdpMid: msg.sdpMid,
            sdpMLineIndex: msg.sdpMLineIndex,
          };
      try {
        await this.pc.addIceCandidate(candidate);
      } catch {
        // Late/duplicate candidates can throw harmlessly; ignore.
      }
    },

    // --- Outbound message builders ---
    buildAnswerMessage(sdp) {
      return { type: "answer", sdp };
    },
    // The Viz Engine WebRTC server expects the ICE fields FLATTENED at the message
    // root, with sdpMid a string — it rejects a null sdpMid outright
    // ("[json.exception.type_error.302] (/sdpMid) type must be string, but is null").
    // A browser candidate can carry sdpMid=null (only sdpMLineIndex set), so coerce
    // to "0" (the bundled m-line id). Nesting the whole candidate object under
    // `candidate` — the old shape — left the root sdpMid null and dropped every
    // trickled candidate, so cross-subnet peers never connected.
    buildIceMessage(candidate) {
      return {
        type: "candidate",
        candidate: candidate.candidate,
        sdpMid: candidate.sdpMid != null ? String(candidate.sdpMid) : "0",
        sdpMLineIndex: candidate.sdpMLineIndex != null ? candidate.sdpMLineIndex : 0,
      };
    },

    sendSignal(obj) {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify(obj));
      }
    },

    scheduleReconnect() {
      if (this.reconnectTimer || this.destroyed) return;
      if (this.retryCount >= this.maxRetries) {
        this.errorMessage = "Não foi possível conectar ao stream WebRTC.";
        return;
      }
      const delay = Math.min(1000 * 2 ** this.retryCount, 8000);
      this.retryCount += 1;
      this.reconnectTimer = setTimeout(() => {
        this.reconnectTimer = null;
        this.connect();
      }, delay);
    },

    fail(message, err) {
      this.errorMessage = message;
      this.$emit("error", { type: "fatal", message, error: err });
    },

    teardown() {
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }
      if (this.ws) {
        this.ws.onopen = this.ws.onmessage = this.ws.onerror = this.ws.onclose = null;
        try { this.ws.close(); } catch { /* noop */ }
        this.ws = null;
      }
      if (this.pc) {
        try { this.pc.close(); } catch { /* noop */ }
        this.pc = null;
      }
      if (this.$refs.video) this.$refs.video.srcObject = null;
    },
  },
};
</script>

<style scoped>
.player-wrapper {
  width: 100%;
  height: 100%;
  overflow: hidden;
  /* transparent — parent (CenaView) owns the background/chroma */
  background: transparent;
}

.player-video {
  /* Full-frame broadcast overlay: fill the element edge-to-edge. `contain` would
     letterbox whenever the element is a hair off 16:9 (e.g. a 1918px-wide box
     scales a 1920x1080 source to 1918x1079, leaving a 1px chroma line at the
     top). `fill` removes any letterbox; source and target are both ~16:9 so the
     stretch is sub-0.1% and imperceptible. */
  width: 100%;
  height: 100%;
  object-fit: fill;
  display: block;
}

.error-message {
  position: absolute;
  bottom: 10px;
  left: 10px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 5px 10px;
  border-radius: 5px;
  font-size: 14px;
}
</style>
